import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getApiUrl } from '../utils/api';

type AppRole = 'admin' | 'end_user';

interface RoleContextType {
  userRole: AppRole | null;
  isAdmin: boolean;
  isEndUser: boolean;
  roleLoading: boolean;
  setUserRole: (role: AppRole | null) => void;
}

const RoleContext = createContext<RoleContextType>({
  userRole: null,
  isAdmin: false,
  isEndUser: false,
  roleLoading: true,
  setUserRole: () => {},
});

export const useRole = () => useContext(RoleContext);

/** Normalize API role strings (including legacy "staff") to app roles. */
export function normalizeUserRole(raw: unknown): AppRole | null {
  if (raw == null) return null;
  const value = String(raw).trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (value === 'admin') return 'admin';
  if (value === 'end_user' || value === 'staff' || value === 'user') return 'end_user';
  return null;
}

export const RoleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userRole, setUserRole] = useState<AppRole | null>(null);
  const [roleLoading, setRoleLoading] = useState(true);
  const { isAuthenticated, token } = useAuth();

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        if (!isAuthenticated || !token) {
          setUserRole(null);
          setRoleLoading(false);
          return;
        }

        setRoleLoading(true);
        const API_URL = getApiUrl();
        const response = await fetch(`${API_URL}/auth/test-token`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const userData = await response.json();
          setUserRole(normalizeUserRole(userData.role));
        } else {
          console.error('Failed to fetch user role:', response.status, response.statusText);
          setUserRole(null);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
        setUserRole(null);
      } finally {
        setRoleLoading(false);
      }
    };

    fetchUserRole();
  }, [isAuthenticated, token]);

  const isAdmin = userRole === 'admin';
  // Treat unresolved/unknown roles as end users so nav is never blank for signed-in users
  const isEndUser = userRole === 'end_user' || (isAuthenticated && !isAdmin && !roleLoading);

  return (
    <RoleContext.Provider
      value={{
        userRole,
        isAdmin,
        isEndUser,
        roleLoading,
        setUserRole,
      }}
    >
      {children}
    </RoleContext.Provider>
  );
};
